import { z } from 'zod';
import { invoiceNumberFormatError } from './numbering.js';
export { patchSchema } from './patch.js';
import { updateMailStyleSchema } from './mailStyle.js';
import {
  CADENCES, CUSTOMER_TYPES, DUNNING_ACTIONS, INVOICE_STATUSES, MAIL_KINDS, PAYMENT_METHODS, PURCHASE_INVOICE_STATUSES, ROLES,
  SCHEDULE_STATUSES, TEMPLATE_KINDS, TEMPLATE_STATUSES, TICKET_PRIORITIES, TICKET_STATUSES, VAT_TREATMENTS,
} from './enums.js';

export const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD');
export const cents = z.number().int();

export const lineSchema = z.object({
  description: z.string().min(1).max(500),
  quantity: z.number().min(0),
  unitPriceCents: cents,
  vatRate: z.number().min(0).max(100),
  discount: z.boolean().default(false),
});
export type LineInputDto = z.infer<typeof lineSchema>;

export const createInvoiceSchema = z.object({
  customerId: z.string().uuid(),
  issuedAt: isoDate.optional(),
  paymentDays: z.number().int().min(0).max(365).optional(),
  reference: z.string().max(200).optional(),
  subject: z.string().max(300).optional().nullable(),
  vatTreatment: z.enum(VAT_TREATMENTS).optional(),
  introText: z.string().max(4000).optional(),
  noteText: z.string().max(4000).optional(),
  lines: z.array(lineSchema).min(1),
});
export type CreateInvoiceDto = z.infer<typeof createInvoiceSchema>;

export const updateInvoiceSchema = createInvoiceSchema.partial();

/**
 * An invoice that already exists on paper — made in an earlier tool — brought
 * in with its own number and PDF. Amounts are what the PDF says; the VAT is
 * taken from the treatment when not given.
 */
export const importInvoiceSchema = z.object({
  customerId: z.string().uuid(),
  number: z.string().min(1).max(50),
  issuedAt: isoDate,
  dueAt: isoDate.optional(),
  paymentDays: z.number().int().min(0).max(365).optional(),
  subject: z.string().max(300).optional().nullable(),
  reference: z.string().max(200).optional().nullable(),
  vatTreatment: z.enum(VAT_TREATMENTS).optional(),
  subtotalCents: cents,
  vatCents: cents.optional(),
  /** Set when the invoice was already paid: it comes in as paid, with one payment on that date. */
  paidAt: isoDate.optional().nullable(),
});
export type ImportInvoiceDto = z.infer<typeof importInvoiceSchema>;

export const recordPaymentSchema = z.object({
  paidAt: isoDate,
  amountCents: cents.positive(),
});

export const linkEboekhoudenSchema = z.object({
  ebInvoiceId: z.number().int().positive(),
});

export const invoiceListQuery = z.object({
  status: z.enum(['all', 'draft', 'sent', 'overdue', 'in_dunning', 'paid']).default('all'),
  customerId: z.string().uuid().optional(),
  q: z.string().max(100).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});

export const contactSchema = z.object({
  id: z.string().uuid().optional(),
  name: z.string().min(1).max(200),
  email: z.string().email(),
  role: z.enum(['primary', 'bcc']).default('bcc'),
  receivesMail: z.boolean().default(true),
});

export const customerSchema = z.object({
  type: z.enum(CUSTOMER_TYPES).default('company'),
  isCustomer: z.boolean().default(true),
  isSupplier: z.boolean().default(false),
  name: z.string().min(1).max(200),
  contactName: z.string().max(200).optional().nullable(),
  email: z.string().email().optional().nullable(),
  phone: z.string().max(50).optional().nullable(),
  address: z.string().max(200).optional().nullable(),
  postcode: z.string().max(20).optional().nullable(),
  city: z.string().max(100).optional().nullable(),
  country: z.string().length(2).default('NL'),
  cocNumber: z.string().max(50).optional().nullable(),
  vatNumber: z.string().max(50).optional().nullable(),
  oin: z.string().max(50).optional().nullable(),
  vatTreatment: z.enum(VAT_TREATMENTS).default('nl_21'),
  paymentDays: z.number().int().min(0).max(365).default(30),
  defaultLayout: z.string().max(100).optional().nullable(),
  revenueAccount: z.string().max(100).optional().nullable(),
  hourlyRateCents: cents.optional().nullable(),
  mileageRateCents: cents.optional().nullable(),
  showPartialPayments: z.boolean().default(false),
  dunningOverride: z.object({
    skipFees: z.boolean().optional(),
    stopAfterStep: z.number().int().min(0).optional(),
    paymentDays: z.number().int().optional(),
    reminderEmail: z.string().email().optional(),
    optOut: z.boolean().optional(),
    note: z.string().max(200).optional(),
  }).nullable().optional(),
  freeField1: z.string().max(200).optional().nullable(),
  freeField2: z.string().max(200).optional().nullable(),
  freeField3: z.string().max(200).optional().nullable(),
  ebRelationId: z.number().int().optional().nullable(),
  contacts: z.array(contactSchema).optional(),
});
export type CustomerDto = z.infer<typeof customerSchema>;

export const scheduleSchema = z.object({
  customerId: z.string().uuid(),
  name: z.string().min(1).max(200),
  cadence: z.enum(CADENCES).default('monthly'),
  dayOfMonth: z.number().int().min(1).max(28).default(1),
  sendOffsetDays: z.number().int().min(-31).max(31).default(0),
  nextRunAt: isoDate,
  status: z.enum(SCHEDULE_STATUSES).default('active'),
  autoMail: z.boolean().default(true),
  spreadRevenue: z.boolean().default(false),
  showPartialPayments: z.boolean().default(false),
  paymentMethod: z.enum(PAYMENT_METHODS).default('bank_transfer'),
  paymentDays: z.number().int().min(0).max(365).optional().nullable(),
  layout: z.string().max(100).optional().nullable(),
  revenueAccount: z.string().max(100).optional().nullable(),
  vatTreatment: z.enum(VAT_TREATMENTS).optional().nullable(),
  nextOnlyText: z.string().max(4000).optional().nullable(),
  introText: z.string().max(4000).optional().nullable(),
  noteText: z.string().max(4000).optional().nullable(),
  code: z.string().max(100).optional().nullable(),
  externalId: z.string().max(100).optional().nullable(),
  orderReference: z.string().max(200).optional().nullable(),
  subject: z.string().max(300).optional().nullable(),
  lines: z.array(lineSchema).min(1),
});
export type ScheduleDto = z.infer<typeof scheduleSchema>;

export const createTicketSchema = z.object({
  customerId: z.string().uuid().optional().nullable(),
  subject: z.string().min(1).max(300),
  body: z.string().min(1).max(20000),
  priority: z.enum(TICKET_PRIORITIES).default('normal'),
  linkedInvoiceId: z.string().uuid().optional().nullable(),
});

export const updateTicketSchema = z.object({
  priority: z.enum(TICKET_PRIORITIES).optional(),
  status: z.enum(TICKET_STATUSES).optional(),
  assigneeUserId: z.string().uuid().nullable().optional(),
  linkedInvoiceId: z.string().uuid().nullable().optional(),
});

export const ticketMessageSchema = z.object({
  body: z.string().min(1).max(20000),
  internal: z.boolean().default(false),
});

export const dunningStepSchema = z.object({
  name: z.string().min(1).max(200),
  offsetDays: z.number().int().min(0).max(365),
  action: z.enum(DUNNING_ACTIONS).default('mail'),
  templateId: z.string().uuid().nullable().optional(),
  feeCents: cents.default(0),
  interestPct: z.number().min(0).max(100).default(0),
});

export const dunningSettingsSchema = z.object({
  sendTime: z.string().regex(/^\d{2}:\d{2}$/),
  skipWeekendsAndHolidays: z.boolean(),
  pauseWhileTicketOpen: z.boolean(),
});

export const templateSchema = z.object({
  name: z.string().min(1).max(200),
  kind: z.enum(TEMPLATE_KINDS),
  status: z.enum(TEMPLATE_STATUSES).default('draft'),
  subject: z.string().max(300).optional().nullable(),
  bodyText: z.string().max(20000).optional().nullable(),
  pdfLayout: z.enum(['standard', 'reverse_charge']).default('standard'),
});

export const companySettingsSchema = z.object({
  name: z.string().min(1).max(200).optional(),
  shortName: z.string().min(1).max(50).optional(),
  contactName: z.string().max(200).optional().nullable(),
  accountHolder: z.string().max(200).optional().nullable(),
  vatNumber: z.string().max(50).optional().nullable(),
  cocNumber: z.string().max(50).optional().nullable(),
  iban: z.string().max(50).optional().nullable(),
  address: z.string().max(200).optional().nullable(),
  postcode: z.string().max(20).optional().nullable(),
  city: z.string().max(100).optional().nullable(),
  country: z.string().length(2).optional(),
  senderEmail: z.string().email().optional(),
  replyToEmail: z.string().email().optional().nullable(),
  /** BCC'd on every outgoing mail, on top of any per-message bcc (e.g. a customer's cc contacts). */
  bccEmail: z.string().email().optional().nullable(),
  phone: z.string().max(50).optional().nullable(),
  invoicePrefix: z.string().min(1).max(20).optional(),
  invoiceNumberFormat: z.string().max(60).refine((p) => invoiceNumberFormatError(p) === null, { error: (iss) => invoiceNumberFormatError(String(iss.input)) ?? 'Invalid pattern' }).optional(),
  nextInvoiceSeq: z.number().int().min(1).optional(),
  invoiceSeqYear: z.number().int().min(2000).max(2100).optional(),
  defaultPaymentDays: z.number().int().min(0).max(365).optional(),
  defaultVatTreatment: z.enum(VAT_TREATMENTS).optional(),
  icpFiling: z.enum(['monthly', 'quarterly', 'none']).optional(),
  ebApiToken: z.string().max(500).optional().nullable(),
  ebTemplateId: z.number().int().optional().nullable(),
  ebRevenueLedgerId: z.number().int().optional().nullable(),
  ebPushInvoices: z.boolean().optional(),
  /** Null resets the company to the built-in layout. */
  invoiceLayoutHtml: z.string().max(100_000).optional().nullable(),
  crmEnabled: z.boolean().optional(),
  crmWeeklyContactCap: z.number().int().min(0).max(10_000).optional(),
  crmMyDataPublic: z.boolean().optional(),
  /** Merged into the stored style: only the fields given change. */
  mailStyle: updateMailStyleSchema.optional(),
});

export const layoutPreviewSchema = z.object({
  layoutHtml: z.string().max(100_000),
  lang: z.enum(['en', 'nl']).default('nl'),
  /** Preview the dunning-letter variant instead of a plain invoice. */
  letter: z.boolean().default(false),
});

/** Global-admin only: a new company gets the built-in templates and dunning ladder. */
export const createCompanySchema = z.object({
  name: z.string().min(1).max(200),
  shortName: z.string().min(1).max(50),
  /** URL/MCP handle, e.g. "hees"; lower-case letters, digits and dashes. */
  slug: z.string().min(2).max(50).regex(/^[a-z0-9][a-z0-9-]*[a-z0-9]$/, 'Use lower-case letters, digits and dashes'),
  senderEmail: z.string().email(),
  replyToEmail: z.string().email().optional().nullable(),
  contactName: z.string().max(200).optional().nullable(),
  country: z.string().length(2).default('NL'),
  invoicePrefix: z.string().min(1).max(20).default('INV'),
  invoiceNumberFormat: z.string().max(60).refine((p) => invoiceNumberFormatError(p) === null, { error: (iss) => invoiceNumberFormatError(String(iss.input)) ?? 'Invalid pattern' }).optional(),
});

export const inviteMemberSchema = z.object({
  email: z.string().email(),
  role: z.enum(ROLES),
  customerId: z.string().uuid().optional().nullable(),
});

export const mailKindSchema = z.enum(MAIL_KINDS);
export const invoiceStatusSchema = z.enum(INVOICE_STATUSES);

/** Edits to a received invoice in the digital dossier, before it is booked. */
export const updatePurchaseInvoiceSchema = z.object({
  /** e-Boekhouden's relation id — there is no local supplier record to point at instead. */
  ebRelationId: z.number().int().positive().nullable().optional(),
  number: z.string().max(100).nullable().optional(),
  date: isoDate.nullable().optional(),
  description: z.string().max(300).nullable().optional(),
  vatTreatment: z.enum(VAT_TREATMENTS).optional(),
  subtotalCents: cents.optional(),
  vatCents: cents.optional(),
  totalCents: cents.optional(),
  /** Only these two are set by hand; `booked` and `duplicate` follow from booking and the duplicate check. */
  status: z.enum(['pending', 'ignored']).optional(),
});
export type UpdatePurchaseInvoiceDto = z.infer<typeof updatePurchaseInvoiceSchema>;

export const bookPurchaseInvoiceSchema = z.object({
  ledgerId: z.number().int().positive(),
  vatTreatment: z.enum(VAT_TREATMENTS).optional(),
  description: z.string().max(300).optional(),
  /** Books it anyway even though it was flagged as a possible duplicate. */
  force: z.boolean().default(false),
});
export type BookPurchaseInvoiceDto = z.infer<typeof bookPurchaseInvoiceSchema>;

export const purchaseInvoiceListQuery = z.object({
  status: z.enum(['all', ...PURCHASE_INVOICE_STATUSES]).default('all'),
  ebRelationId: z.coerce.number().int().optional(),
  q: z.string().max(100).optional(),
  limit: z.coerce.number().int().min(1).max(500).default(200),
  offset: z.coerce.number().int().min(0).default(0),
});

/** The digital dossier export: every sent and received invoice, with the PDF as the record. */
export const dossierExportQuery = z.object({
  from: isoDate.optional(),
  to: isoDate.optional(),
  direction: z.enum(['all', 'received', 'sent']).default('all'),
});

/** How far back to check e-Boekhouden for received invoices with no archived PDF — defaults to a year, server-side. */
export const dossierGapsQuery = z.object({
  since: isoDate.optional(),
});

/** A long-lived MCP bearer token for an unattended agent, acting as one member of one company. */
export const createMcpTokenSchema = z.object({
  /** What it is for, e.g. "Paperclip outreach agent". */
  name: z.string().trim().min(1).max(200),
  /** The member it acts as: an admin or staff member of this company. */
  email: z.string().trim().toLowerCase().email(),
  /** Omitted: valid until revoked. */
  expiresInDays: z.number().int().min(1).max(3650).optional(),
});
